import { formatMonth } from "./dashboardData";

/**
 * Agregações das telas de detalhe. O que cada tipo de crédito preenche varia
 * bastante (frete vem sem produto e NCM, mas com rota; UF do fornecedor pode vir
 * vazia), então cada corte só existe quando há dado para ele.
 */

// Lê direto do texto ISO: new Date("2025-05-15") é meia-noite UTC e mostraria o
// dia anterior no fuso do Brasil.
export const formatDate = (value) => {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : "—";
};

export const formatRate = (value) => (value || value === 0
  ? `${Number(value).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%`
  : "—");

export const titleCase = (text) => {
  const s = String(text || "").toLowerCase();
  return s ? s[0].toUpperCase() + s.slice(1) : "";
};

const monthOf = (value) => {
  const iso = /^(\d{4}-\d{2})/.exec(String(value || ""));
  return iso ? iso[1] : "";
};

const dateRange = (values) => {
  const dates = values.map((v) => String(v || "").slice(0, 10)).filter(Boolean).sort();
  return { first: dates[0] || "", last: dates[dates.length - 1] || "" };
};

// Agrupa somando o valor; ignora chaves vazias
const rankBy = (rows, keyOf, valueOf) => {
  const groups = {};
  rows.forEach((row) => {
    const key = keyOf(row);
    if (!key) return;
    const group = groups[key] || (groups[key] = { key, total: 0, count: 0 });
    group.total += valueOf(row);
    group.count += 1;
  });
  return Object.values(groups).sort((a, b) => b.total - a.total);
};

// ─── Tela do tipo de crédito ────────────────────────────────────────────────
export const buildCreditoStats = (fornecedores, resumo = {}) => {
  const sorted = [...fornecedores].sort((a, b) => (b.totalCredito || 0) - (a.totalCredito || 0));
  const fornecedorTotal = sorted.reduce((sum, f) => sum + (f.totalCredito || 0), 0);
  const total = resumo.totalCredito || fornecedorTotal;
  const base = resumo.totalBase || sorted.reduce((sum, f) => sum + (f.totalBase || 0), 0);

  // Quantos fornecedores respondem por 80% do valor
  let cumulative = 0;
  let suppliersFor80 = 0;
  sorted.some((f) => {
    cumulative += f.totalCredito || 0;
    suppliersFor80 += 1;
    return cumulative >= fornecedorTotal * 0.8;
  });

  const shareOfTop = (n) => (fornecedorTotal > 0
    ? (sorted.slice(0, n).reduce((sum, f) => sum + (f.totalCredito || 0), 0) / fornecedorTotal) * 100
    : 0);
  const period = dateRange([...sorted.map((f) => f.periodoInicio), ...sorted.map((f) => f.periodoFim)]);

  return {
    sorted,
    total,
    base,
    // resumo.totalItens para em 10.000; a soma por fornecedor é a contagem real
    items: sorted.reduce((sum, f) => sum + (f.quantidade || 0), 0),
    effectiveRate: base > 0 ? (total / base) * 100 : null,
    suppliersFor80,
    concentration: [1, 5, 10].filter((n) => n === 1 || sorted.length > n).map((n) => ({ n, share: shareOfTop(n) })),
    byRate: rankBy(sorted, (f) => (f.mediaAliquota || f.mediaAliquota === 0 ? formatRate(f.mediaAliquota) : ""), (f) => f.totalCredito || 0),
    byCfop: rankBy(sorted, (f) => {
      if (!f.cfops?.length) return "";
      return f.cfops.length === 1 ? f.cfops[0] : "Vários CFOPs";
    }, (f) => f.totalCredito || 0),
    byUf: rankBy(sorted, (f) => f.uf, (f) => f.totalCredito || 0),
    firstDate: period.first,
    lastDate: period.last,
  };
};

// ─── Tela do fornecedor ─────────────────────────────────────────────────────
const creditOf = (item) => item.creditoRecuperavel || 0;

export const buildItemStats = (items) => {
  const documents = {};
  items.forEach((item, index) => {
    const key = item.nfe || `sem-documento-${index}`;
    const doc = documents[key] || (documents[key] = { key, nfe: item.nfe || "", date: item.dataEmissao, total: 0, base: 0, items: [] });
    doc.total += creditOf(item);
    doc.base += item.valorProduto || 0;
    doc.items.push(item);
  });

  const months = rankBy(items, (i) => monthOf(i.dataEmissao), creditOf)
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((m) => ({ key: m.key, label: formatMonth(m.key), credito: m.total, count: m.count }));

  const peak = months.reduce((best, m) => (!best || m.credito > best.credito ? m : best), null);

  return {
    months,
    peak,
    documents: Object.values(documents).sort((a, b) => b.total - a.total),
    products: rankBy(items, (i) => i.descricao?.trim(), creditOf),
    ncms: rankBy(items, (i) => i.ncm?.trim(), creditOf),
    cfops: rankBy(items, (i) => i.cfop, creditOf),
    rates: rankBy(items, (i) => (i.aliquotaAplicavel || i.aliquotaAplicavel === 0 ? formatRate(i.aliquotaAplicavel) : ""), creditOf),
    routes: rankBy(items, (i) => (i.ufOrigem || i.ufDestino ? `${i.ufOrigem || "?"} → ${i.ufDestino || "?"}` : ""), creditOf),
    operations: rankBy(items, (i) => titleCase(i.operacao), creditOf),
    statuses: rankBy(items, (i) => titleCase(i.status), creditOf),
  };
};
